import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import StaffListView from '../components/staff/StaffListView';
import SimpleShiftBoard from '../components/staff/SimpleShiftBoard';
import { StaffInfo } from '../types/staff';
import { usePageTitle } from '../hooks/usePageTitle';
import logger from '../utils/logger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `staff-tab-${index}`,
    'aria-controls': `staff-tabpanel-${index}`,
  };
}

// シンプルなスタッフデータの型定義（互換性のため）
interface SimpleStaff {
  id: string;
  name: string;
  email: string;
  phone: string;
  position: 'owner' | 'manager' | 'senior' | 'junior' | 'trainee';
  status: 'active' | 'vacation' | 'sick' | 'inactive';
  joinDate: string;
  skills?: string[];
}

// シンプルなモックスタッフデータ（SimpleShiftBoard用）
const mockStaffData: SimpleStaff[] = [
  {
    id: 'staff-1',
    name: '佐藤 美咲',
    email: 'misaki.sato@salon.com',
    phone: '090-1234-5678',
    position: 'manager',
    status: 'active',
    joinDate: '2022-04-01',
    skills: [],
  },
  {
    id: 'staff-2',
    name: '田中 健太',
    email: 'kenta.tanaka@salon.com',
    phone: '090-2345-6789',
    position: 'senior',
    status: 'active',
    joinDate: '2023-01-15',
    skills: [],
  },
  {
    id: 'staff-3',
    name: '山田 花子',
    email: 'hanako.yamada@salon.com',
    phone: '090-3456-7890',
    position: 'junior',
    status: 'active',
    joinDate: '2023-08-01',
    skills: [],
  },
  {
    id: 'staff-4',
    name: '鈴木 太郎',
    email: 'taro.suzuki@salon.com',
    phone: '090-4567-8901',
    position: 'trainee',
    status: 'active',
    joinDate: '2024-01-10',
    skills: [],
  },
  {
    id: 'staff-5',
    name: '高橋 優子',
    email: 'yuko.takahashi@salon.com',
    phone: '090-5678-9012',
    position: 'senior',
    status: 'vacation',
    joinDate: '2022-09-15',
    skills: [],
  },
];

const Staff: React.FC = () => {
  usePageTitle('スタッフ管理');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [value, setValue] = useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Handle staff selection from list view
  const handleStaffSelect = (staff: StaffInfo) => {
    // TODO: Could switch to detail view or open detail dialog
    logger.log('Selected staff:', staff);
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 }, 
      bgcolor: 'background.default',
      minHeight: '100vh',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant={isMobile ? 'h5' : 'h4'} 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
          }}
        >
          スタッフ管理
        </Typography>
        
        <Box 
          sx={{ 
            width: 60,
            height: 4,
            bgcolor: 'primary.main',
            borderRadius: 2,
            mt: 2,
          }}
        />
      </Box>

      {/* タブシステム */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="スタッフ管理タブ"
          variant={isMobile || isTablet ? "scrollable" : "standard"}
          scrollButtons={isMobile || isTablet ? "auto" : false}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: isMobile ? 56 : 64,
              textTransform: 'none',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              fontWeight: 500,
              px: isMobile ? 1 : 2,
            },
            '& .MuiTab-iconWrapper': {
              marginRight: isMobile ? 0.5 : 1,
            },
          }}
        >
          <Tab
            label={isMobile ? "一覧" : "スタッフ一覧"}
            icon={<PeopleIcon />}
            iconPosition="start"
            {...a11yProps(0)}
          />
          <Tab
            label={isMobile ? "シフト" : "シフト管理"}
            icon={<ScheduleIcon />}
            iconPosition="start"
            {...a11yProps(1)}
          />
        </Tabs>

        <Box sx={{ 
          bgcolor: 'background.paper',
          minHeight: 'calc(100vh - 200px)',
        }}>
          {/* スタッフ一覧タブ */}
          <TabPanel value={value} index={0}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StaffListView onStaffSelect={handleStaffSelect} />
            </motion.div>
          </TabPanel>

          {/* シフト管理タブ */}
          <TabPanel value={value} index={1}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ 
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                p: { xs: 2, sm: 3, md: 4 },
                overflow: 'hidden',
                isolation: 'isolate',
              }}>
                <SimpleShiftBoard staffList={mockStaffData} />
              </Box>
            </motion.div>
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default Staff;